import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import CreatableSelect from "react-select/creatable";

import CreateOptionLabel from "./CreateOptionLabel";
import {isCreateOptionCandidate, matchesSearch, shouldProposeCreation} from "./optionMatching";

MappingCreatableSelect.propTypes = {
    id: PropTypes.string,
    options: PropTypes.arrayOf(PropTypes.object),
    placeholder: PropTypes.node,
    defaultInputValue: PropTypes.string,
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
    onCreateOption: PropTypes.func,
};

/**
 * Mapping select with an optional "create this value" entry: when
 * `onCreateOption` is provided, a creation entry replaces "No options" as soon
 * as the typed value matches no existing option (accent/case insensitive).
 * Without `onCreateOption` it behaves as a regular select.
 */
function MappingCreatableSelect({id, options, placeholder, defaultInputValue, onChange, onBlur, onCreateOption}) {
    const isCreationEnabled = onCreateOption instanceof Function;
    const [inputValue, setInputValue] = useState('');

    // react-select v3 only computes its "create" entry on input CHANGES, never
    // on mount: an initial value must be applied after mount to trigger it
    useEffect(() => {
        if (defaultInputValue) {
            setInputValue(defaultInputValue);
        }
    }, [defaultInputValue]);

    function handleInputChange(newValue, meta) {
        if ('input-change' === meta.action) {
            setInputValue(newValue);
        }
    }

    return (
        <CreatableSelect
            defaultMenuIsOpen={true}
            autoFocus={true}
            id={id}
            options={options}
            placeholder={placeholder}
            inputValue={inputValue}
            onInputChange={(newValue, meta) => handleInputChange(newValue, meta)}
            onChange={(value) => onChange(value)}
            onBlur={onBlur}
            onCreateOption={onCreateOption}
            filterOption={(candidate, search) => isCreateOptionCandidate(candidate) || matchesSearch(candidate.label, search)}
            isValidNewOption={(inputValue, selectValue, selectOptions) => isCreationEnabled && shouldProposeCreation(selectOptions, inputValue)}
            formatCreateLabel={(inputValue) => <CreateOptionLabel value={inputValue} />}
            styles={{
                placeholder: (baseStyles) => ({
                    ...baseStyles,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }),
            }}
        />
    );
}

export default MappingCreatableSelect;
